import { Component, OnInit } from '@angular/core';
import { IPageHeadline } from 'app/modules/shared/classes/page-title-parameters';
import { DisplayOptions } from 'app/modules/shared/interfaces/display-options';
import { ReusableFunctions } from 'app/modules/shared/classes/reusable-functions';
import { Router } from '@angular/router';
import { NoDataActionType } from 'app/modules/shared/interfaces/no-data';
import {
    IRankingEntityProperty,
    IRankingsData,
    IRanksParameters,
} from '../../../performance/interfaces/performance-rankings';
import { RankingsService } from '../../../performance/services/rankings.service';
import { ITableColumns } from '../../../reports/interfaces/reporting-data';
import {
    IPaginatedDataInfo,
    ITableColumnProperties,
    ITableDataProperties,
} from '../../../shared/components/common-kendo-table/common-kendo-table.component';
import { TableInlineChartOptions } from '../../../shared/classes/table-inline-chart-options';
import {
    IChartParameters,
    SparklineChartOptions,
} from 'app/modules/shared/classes/sparkline-chart-options';

@Component({
    selector: 'rankings-by-partner',
    templateUrl: './rankings-by-partner.component.html',
    styleUrls: ['./rankings-by-partner.component.scss'],
})
export class RankingsByPartnerComponent implements OnInit {
    pageHeadline: IPageHeadline;
    displayOptions: DisplayOptions[];
    rankingData: IRankingsData[];
    rankSearchTerm: string;
    datePeriod: string;
    ymc: string;
    parameters: IRanksParameters;
    selectedPartner: IRankingEntityProperty[];
    partnerList: IRankingEntityProperty[];
    partnerListSettings: object;
    tableDataProperties: ITableDataProperties;
    action: NoDataActionType;
    tableColumnProperties: ITableColumnProperties;
    periodDates: string[];
    currentPeriodData: IRankingsData[];
    fullDataset: IRankingsData[];
    paginatedData: IRankingsData[];
    spco: SparklineChartOptions;
    loading: boolean;

    constructor(
        private _router: Router,
        private _reusableFunctions: ReusableFunctions,
        private rankingsService: RankingsService
    ) {}

    ngOnInit() {
        this.displayOptions = this._reusableFunctions.getCurrentDisplayOptions(this._router.url);
        this.selectedPartner = [];
        this.partnerList = [];
        this.rankingData = [];
        this.rankSearchTerm = '';
        this.action = NoDataActionType.pageStart;

        this.tableDataProperties = {
            action: this.action,
            gridData: [],
            searchTerm: this.rankSearchTerm,
            componentTitle: 'Rankings by Partner',
            initialPageSize: 10,
            initialSkip: 0,
            height: 700,
            recordSelection: [],
            kendoGridSelectById: '',
            hasData: false,
            isDataLoaded: false,
            pageable: true,
            useLGScroll: false,
        };

        this.partnerListSettings = {
            singleSelection: true,
            text: 'Select Partner',
            enableSearchFilter: false,
            showCheckbox: false,
            labelKey: 'partnerType',
            primaryKey: 'partnerTypeKey',
            enableFilterSelectAll: false,
        };

        this.pageHeadline = {
            module: 'leads-partners',
            moduleIcon: 'fa-handshake',
            pageTitle: 'Rankings by Partner',
            linkArray: [
                {
                    moduleLink: 'leads-partners/insights',
                    linkTitle: 'Leads & Partners Insights',
                    showLink: true,
                },
            ],
        };

        this.setDisplayOptionsSelected();
        this.setParameters();
        this.currentPeriodData = [];
        this.spco = new SparklineChartOptions(this._reusableFunctions);
        this.initiateTableProperties();
        this.getPartners();
        this.getData();
    }

    initiateTableProperties() {
        this.tableDataProperties = {
            action: this.action,
            gridData: [],
            searchTerm: '',
            componentTitle: 'KPIs for Each Employee',
            initialPageSize: 10,
            initialSkip: 0,
            height: 700,
            recordSelection: [],
            kendoGridSelectById: '',
            hasData: false,
            isDataLoaded: true,
            pageable: true,
            useLGScroll: false,
        };

        let columnProperties: ITableColumns[] = [
            {
                fieldId: 'rank',
                fieldName: 'Rank',
                isHyperlink: false,
                fieldOrder: 0,
                isSortable: true,
                defaultSorting: 'asc',
                headerAlignment: 'centre',
                dataAlignment: 'centre',
                dataType: 'number',
                width: 65,
                filterable: false,
                filter: 'numeric',
            },
            {
                fieldId: 'partnerName',
                fieldName: 'Partner',
                isHyperlink: false,
                hyperLinkTo_FieldId: 'partnerIdentifier',
                isHierarchyEntity: true,
                fieldOrder: 1,
                isSortable: true,
                headerAlignment: 'left',
                dataAlignment: 'left',
                dataType: 'string',
                width: 130,
                filterable: false,
                filter: 'text',
            },
            {
                fieldId: 'scoreValuesSparkline',
                fieldName: 'Score Trend',
                fieldOrder: 2,
                isSortable: false,
                headerAlignment: 'left',
                dataAlignment: 'left',
                dataType: 'chart',
                width: 160,
                filterable: false,
                hideIf: 'excel',
                chartProperties: {
                    chart: null,
                    dataType: 'number',
                    chartToFieldId: 'rank',
                    chartDataField: 'wgtScore',
                    inComponentChartBuilding: false,
                    tooltipProperties: {
                        labelField: 'Score',
                        datatype: null,
                        precision: 3,
                    },
                },
            },
            {
                fieldId: 'avgWgtScore',
                fieldName: 'Avg Score',
                isHyperlink: false,
                isHierarchyEntity: false,
                fieldOrder: 3,
                isSortable: true,
                headerAlignment: 'center',
                dataAlignment: 'center',
                dataType: 'number',
                decimalPlaces: 2,
                width: 80,
                filterable: false,
                filter: 'numeric',
            },
            {
                fieldId: 'volumeValuesSparkline',
                fieldName: 'Volume Trend',
                fieldOrder: 4,
                isSortable: false,
                headerAlignment: 'left',
                dataAlignment: 'left',
                dataType: 'chart',
                width: 160,
                filterable: false,
                hideIf: 'excel',
                chartProperties: {
                    chart: null,
                    dataType: 'number',
                    chartToFieldId: 'rank',
                    chartDataField: 'volume',
                    inComponentChartBuilding: false,
                    tooltipProperties: {
                        labelField: 'Volume',
                        datatype: 'currency',
                        precision: 0,
                    },
                },
            },
            {
                fieldId: 'totalVolume',
                fieldName: 'Total Volume',
                isHyperlink: false,
                isHierarchyEntity: false,
                fieldOrder: 5,
                isSortable: true,
                headerAlignment: 'center',
                dataAlignment: 'center',
                dataType: 'money',
                decimalPlaces: 0,
                width: 100,
                filterable: false,
                filter: 'numeric',
            },
            {
                fieldId: 'unitValuesSparkline',
                fieldName: 'Units Trend',
                fieldOrder: 6,
                isSortable: false,
                headerAlignment: 'left',
                dataAlignment: 'center',
                dataType: 'chart',
                width: 160,
                filterable: false,
                hideIf: 'excel',
                chartProperties: {
                    chart: null,
                    dataType: 'number',
                    chartToFieldId: 'rank',
                    chartDataField: 'units',
                    inComponentChartBuilding: false,
                    tooltipProperties: {
                        labelField: 'Units',
                        datatype: null,
                        precision: 0,
                    },
                },
            },
            {
                fieldId: 'totalUnits',
                fieldName: 'Total Units',
                isHyperlink: false,
                isHierarchyEntity: false,
                fieldOrder: 7,
                isSortable: true,
                headerAlignment: 'center',
                dataAlignment: 'center',
                dataType: 'number',
                decimalPlaces: 0,
                width: 60,
                filterable: false,
                filter: 'numeric',
            },
        ];

        this.tableColumnProperties = {
            columnProperties: columnProperties,
        };
    }

    gridChangeEvent(event: IPaginatedDataInfo) {
        this.setColumnFormat();
        this.updateTableColumnProperties();
        this.tableColumnProperties = Object.assign({}, this.tableColumnProperties);
        this.paginatedData = event?.gridData;

        let chartParams: IChartParameters = {
            paginatedData: this.paginatedData,
            fullDataset: this.fullDataset,
            toGroupByColumnHeader: 'partnerName',
            calculateChange: true,
            columnList: this.tableColumnProperties.columnProperties,
            chartIdColumn: 'rank',
            chartType: 'area',
            color: 'rgb(124, 181, 236)',
        };

        setTimeout(() => {
            this.spco.setCharts(chartParams);
        }, 0);
    }

    updateTableColumnProperties() {
        let hierarchyFieldInf = this.tableColumnProperties.columnProperties.find(
            (colInf) => colInf.backUpFieldId === 'hierarchyField'
        );
    }

    setColumnFormat() {
        let columnsTobeFormatted = ['wgtScore'];
        let columnProperties = this.tableColumnProperties.columnProperties;

        columnsTobeFormatted.forEach((fieldId) => {
            let valueColumn = columnProperties.find((col) => col.fieldId === fieldId);
            if (valueColumn) {
                let format = this.currentPeriodData[0]?.format ?? valueColumn.dataType;
                let precision = this.currentPeriodData[0]?.precision ?? valueColumn.decimalPlaces;
                valueColumn.dataType = format as any;
                valueColumn.decimalPlaces = precision;
            }
        });
    }

    toggleChange(isChecked: boolean) {
        this.updateTableChartsHeaders(isChecked);
    }

    updateTableChartsHeaders(isChecked = false) {
        let suffix = 'Trend';
        let chartType = 'area';
        let calculateChange = false;
        if (isChecked) {
            suffix = 'Change';
            chartType = 'column';
            calculateChange = true;
        }

        let tableColumnProperties = this.tableColumnProperties;

        tableColumnProperties.columnProperties.map((col) => {
            if (col.dataType === 'chart') {
                let names = col.fieldName.split(' ');
                col.fieldName = names[0] + ' ' + suffix;

                if (col.chartProperties) {
                    col.chartProperties.chartOptionParams['seriesType'] = chartType;
                    col.chartProperties.chartOptionParams['dataType'] =
                        col.chartProperties.dataType;
                    col.chartProperties.chartOptionParams['calculateChange'] = calculateChange;
                }
            }
        });
        this.tableColumnProperties = Object.assign({}, tableColumnProperties);
    }

    setParameters() {
        this.parameters = {
            datePeriod: this.datePeriod,
            entityType: '',
            entityCode: this.selectedPartner[0]?.partnerCode,
            hFilterJSON: '[]',
            ymc: this.ymc,
        };
    }

    setDisplayOptionsSelected() {
        let datePeriodInf = this.displayOptions.find(
            (item) => item.optionKey.toLowerCase() === 'daterangerolling'
        );
        if (datePeriodInf && datePeriodInf.selectedOptions) {
            this.datePeriod = datePeriodInf.selectedOptions;
        }

        let ymc = this.displayOptions.find(
            (item) => item.optionKey.toLowerCase() === 'scorecardasof'
        );
        if (ymc && ymc.selectedOptions) {
            this.ymc = ymc.selectedOptions;
        }
    }

    applyDisplayOptions(event: DisplayOptions[]) {
        this.displayOptions = event;
        this.action = NoDataActionType.filter;
        this.setDisplayOptionsSelected();
        this.getData();
    }

    getPartners() {
        this.rankingsService.getPartnerList(this.parameters).subscribe(
            (res) => {
                this.partnerList = res;
            },
            (err) => {
                throw new Error(err);
            }
        );
    }

    getData() {
        this.tableDataProperties.gridData = [];
        if (this.selectedPartner[0]) {
            this.tableDataProperties.hasData = false;
            this.tableDataProperties.isDataLoaded = false;
        } else {
            this.tableDataProperties.hasData = false;
            this.tableDataProperties.isDataLoaded = true;
        }

        if (this.ymc && this.selectedPartner.length > 0) {
            this.setParameters();
            this.rankingsService.getPartnersRank(this.parameters).subscribe(
                (res) => {
                    this.fullDataset = res;
                    this.currentPeriodData = res.filter((x) => x.isCurrentPeriod === true);
                    this.tableDataProperties.gridData = this.currentPeriodData;
                    this.tableDataProperties.hasData = true;
                    this.tableDataProperties.isDataLoaded = true;
                    this.tableDataProperties = Object.assign({}, this.tableDataProperties);
                    this.loading = false;
                },
                (err) => {
                    this.tableDataProperties.gridData = [];
                    this.tableDataProperties.hasData = false;
                    this.tableDataProperties.isDataLoaded = true;
                    this.tableDataProperties.action = NoDataActionType.error;
                    this.tableDataProperties = Object.assign({}, this.tableDataProperties);
                }
            );
        }
    }

    setPageTitle() {
        if (this.selectedPartner && this.selectedPartner.length > 0) {
            this.pageHeadline.pageTitle = 'Rankings by ' + this.selectedPartner[0].partnerType;
        } else {
            this.pageHeadline.pageTitle = 'Rankings by Partner';
        }
        this.tableDataProperties.componentTitle = this.pageHeadline.pageTitle;
    }

    searchRows(event) {
        this.rankSearchTerm = event;
        this.action = NoDataActionType.search;
        setTimeout(() => {
            this.tableDataProperties.searchTerm = this.rankSearchTerm;
            this.tableDataProperties = Object.assign({}, this.tableDataProperties);
        }, 1000);
    }

    onSelectionChange(event) {
        this.action = NoDataActionType.filter;
        this.setParameters();
        this.getData();
    }

    onDeSelectionChange(event) {
        this.selectedPartner = [event];
    }
}
